import _ from 'lodash';
import io from 'socket.io-client';

import common from '@common';

import * as selectors from './selectors';

const { tokenUtils } = common.utils;
export const { WS_BASE_URL } = process.env;

/**
 * @description Object of available socket namespaces
 * @type {Object}
 */
export const NAMESPACES = {
  MESSAGING: 'messaging',
};

/**
 * @description Create socket instances by namespaces
 * @return {Object}
 */
export const createInstances = () =>
  _.keyBy(
    _.map(NAMESPACES, namespace => {
      const instance = io(`${WS_BASE_URL}/${namespace}`, {
        autoConnect: false,
        rejectUnauthorized: true,
      });

      return {
        instance,
        namespace,
      };
    }),
    'namespace',
  );

const sockets = createInstances();

/**
 * @description Subscribe event and handle it by action or actionType
 * @param {String} namespace
 * @param {String} event
 * @param {String} actionType - dispatching action with this type and response(payload)
 * @param {Function} callbackAction - dispatching this action
 * @return {Function}
 */
export const subscribeEvent = (
  namespace,
  event,
  actionType = null,
  callbackAction = () => {},
) => dispatch =>
  dispatch({
    type: 'sockets/SUBSCRIBE_EVENT',
    socket: {
      event,
      handle: actionType || (payload => dispatch(callbackAction(payload))),
      namespace,
    },
  });

/**
 * @description Unsubscribe event
 * @param {String} namespace
 * @param {String} event
 * @return {Function}
 */
export const unsubscribeEvent = (namespace, event) => dispatch =>
  dispatch({
    type: 'sockets/UNSUBSCRIBE_EVENT',
    socket: {
      event,
      leave: true,
      namespace,
    },
  });

/**
 * @description Emit event
 * @param {String} namespace
 * @param {String} event
 * @param {String|Number|Object|Array} payload
 * @param {String} actionType
 * @return {Function}
 */
export const emitEvent = (namespace, event, payload, actionType = null) => ({
  type: actionType,
  payload,
  socket: {
    emit: true,
    event,
    namespace,
  },
});

/**
 * @description Disconnect sockets
 */
export const disconnectSockets = () => {
  _.forEach(sockets, socket => {
    socket.instance.disconnect();
  });
};

/**
 * @description Open sockets
 * @param {Array} namespaces - array of accessible namespaces
 * @param {String} jwt
 */
export const openSockets = (namespaces, jwt = null) => {
  _.forEach(sockets, socket => {
    const socketNamespace = _.get(socket, 'namespace');
    if (_.includes(namespaces, socketNamespace)) {
      socket.instance.io.opts.query = {
        jwt: jwt || tokenUtils.getAuthToken(),
      };
      socket.instance.open();
    }
  });
};

export { selectors };

export default sockets;
